import { StoreApi } from 'zustand';
import { createContext } from 'zustand-utils';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import { StateCreator } from 'zustand/vanilla';

import { DEFAULT_FEATURE_FLAGS, IFeatureFlags } from '@/config/feature-flags';
import { createDevtools } from '@/store/middleware/create-devtools';
import { GlobalServerConfig } from '@/types_cp/server-config';
import { merge } from '@/utils_cp/merge';
import { StoreApiWithSelector } from '@/utils_cp/zustand';

import { ServerConfigAction, createServerConfigSlice } from './action';

interface ServerConfigState {
  featureFlags: IFeatureFlags;
  isMobile?: boolean;
  serverConfig: GlobalServerConfig;
}

const initialState: ServerConfigState = {
  featureFlags: DEFAULT_FEATURE_FLAGS,
  serverConfig: { aiProvider: {}, telemetry: {} },
};


export interface ServerConfigStore extends ServerConfigState, ServerConfigAction {}

type CreateStore = (
  initState: Partial<ServerConfigStore>,
) => StateCreator<ServerConfigStore, [['zustand/devtools', never]]>;

const createStore: CreateStore =
  (runtimeState) =>
  (...params) => ({
    ...merge(initialState, runtimeState),
    ...createServerConfigSlice(...params),
  });


let store: StoreApi<ServerConfigStore>;

declare global {
  interface Window {
    global_serverConfigStore: StoreApi<ServerConfigStore>;
  }
}

const devtools = createDevtools('serverConfig');

export const initServerConfigStore = (initState: Partial<ServerConfigStore>) =>
  createWithEqualityFn<ServerConfigStore>()(devtools(createStore(initState || {})), shallow);

export const createServerConfigStore = (initState?: Partial<ServerConfigStore>) => {
  // make sure there is only one store
  if (!store) {
    store = createWithEqualityFn<ServerConfigStore>()(
      devtools(createStore(initState || {})),
      shallow,
    );

    if (typeof window !== 'undefined') {
      window.global_serverConfigStore = store;
    }
  }

  return store;
};

export const { useStore: useServerConfigStore, Provider } =
  createContext<StoreApiWithSelector<ServerConfigStore>>();
